import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "../data/content";

const links = [
  { to: "/", label: "Home" },
  { to: "/cabins", label: "Cabins" },
  { to: "/marina", label: "Marina" },
  { to: "/boating", label: "Boating" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[var(--ec-cream)]/95 backdrop-blur-md shadow-[0_4px_20px_-12px_rgba(15,42,71,0.25)]"
          : "bg-gradient-to-b from-[rgba(8,24,42,0.55)] to-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 flex items-center justify-between h-20">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="nav-logo-link"
          onClick={() => setOpen(false)}
        >
          <img
            src={SITE.logo}
            alt="Elk Creek Marina logo"
            className={`h-14 w-14 object-contain rounded-xl p-1 transition-all duration-300 ${
              scrolled ? "bg-white shadow-sm" : "bg-white/95 shadow-md"
            }`}
          />
          <span
            className={`hidden sm:flex flex-col leading-tight transition-colors ${
              scrolled ? "text-[var(--ec-navy)]" : "text-white"
            }`}
            style={!scrolled ? { textShadow: "0 1px 14px rgba(8,24,42,0.7)" } : {}}
          >
            <span className="font-display text-lg tracking-tight">Elk Creek Marina</span>
            <span className="text-[11px] uppercase tracking-[0.18em] opacity-95">
              Lake Tenkiller, OK
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  scrolled ? "text-[var(--ec-navy)]" : "text-white"
                } ${isActive ? "opacity-100" : "opacity-80 hover:opacity-100"}`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-[2px] rounded-full transition-all duration-300 ${
                      isActive
                        ? "opacity-100 scale-x-100 bg-[var(--ec-lake)]"
                        : "opacity-0 scale-x-0 bg-[var(--ec-lake)]"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
          <a
            href={`tel:${SITE.phoneRaw}`}
            data-testid="nav-call-button"
            className={`ml-3 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold btn-lift ${
              scrolled
                ? "bg-[var(--ec-navy)] text-white hover:bg-[var(--ec-lake)]"
                : "bg-white text-[var(--ec-navy)] hover:bg-[var(--ec-sand-soft)]"
            }`}
          >
            <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
          </a>
        </nav>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
          data-testid="nav-mobile-toggle"
          className={`lg:hidden p-2 rounded-md ${
            scrolled ? "text-[var(--ec-navy)]" : "text-white"
          }`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <nav
        id="mobile-menu"
        aria-label="Mobile"
        inert={!open}
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        } bg-[var(--ec-cream)] border-t border-[var(--ec-sand)]`}
        data-testid="nav-mobile-panel"
      >
        <div className="px-5 py-4 flex flex-col">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `py-3 border-b border-[var(--ec-sand)]/60 text-[var(--ec-navy)] font-medium ${
                  isActive ? "text-[var(--ec-lake)]" : ""
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={`tel:${SITE.phoneRaw}`}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--ec-navy)] text-white px-5 py-3 font-semibold btn-lift"
            data-testid="mobile-nav-call-button"
          >
            <Phone className="w-4 h-4" /> Call {SITE.phoneDisplay}
          </a>
        </div>
      </nav>
    </header>
  );
}
